import React, { useState, useEffect } from 'react';
import {
  MdSchedule,
  MdAccessTime,
  MdPeople,
  MdCheckCircle,
  MdAssignment,
  MdCalendarToday,
  MdCheck,
  MdAdd
} from 'react-icons/md';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/card';
import { Clock } from 'lucide-react';
import { Button } from '@mui/material';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../lib/constants';
// Add CSS for button animation
const buttonStyles = `
  .review-button {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
  .review-button:hover {
    transform: scale(1.05);
  }
  .review-button:active {
    transform: scale(0.95);
  }
  .review-button--clicked {
    animation: fadeOut 0.3s ease forwards;
  }
  @keyframes fadeOut {
    0% { opacity: 1; transform: scale(1); }
    100% { opacity: 0; transform: scale(0.8); }
  }
`;

const NurseDashboard = () => {
  const { showNotification } = useNotification();
  const [nurseTodos, setNurseTodos] = useState([
    { id: 1, task: "Pre-op checklist for John Doe (UHID: 12345)", completed: false, time: "08:30" },
    { id: 2, task: "Prepare OT-1 for laparoscopic surgery", completed: false, time: "08:45" },
    { id: 3, task: "Verify consent forms for Jane Smith", completed: true, time: "07:30" },
    { id: 4, task: "Check blood products availability", completed: false, time: "09:00" }
  ]);

  const [newTodo, setNewTodo] = useState('');
  const [nurseNotifications, setNurseNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todaysOperations, setTodaysOperations] = useState([]);
  const [otUtilizationData, setOtUtilizationData] = useState([]);
  const [otStatusData, setOtStatusData] = useState([]);
  const [count, setCount] = useState(0);
  const [clickedButtons, setClickedButtons] = useState(new Set()); // Track clicked buttons for animation



  const updateFormStatus = async (uhid, idx) => {
    try {
      // Add to clickedButtons to trigger animation
      setClickedButtons(prev => new Set(prev).add(idx));

      const response = await fetch(`${API_BASE_URL}/update_notification_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uhid, status: 'reviewed' }),
      });

      const result = await response.json();

      if (response.ok) {
        // Remove notification from state after successful update
        setNurseNotifications(prev => prev.filter((_, i) => i !== idx));
        showNotification('success', result.message);
      } else {
        showNotification('error', `Error: ${result.error}`);
        // Remove from clickedButtons on failure
        setClickedButtons(prev => {
          const newSet = new Set(prev);
          newSet.delete(idx);
          return newSet;
        });
      }
    } catch (error) {
      console.error("Request failed", error);
      showNotification('error', "Failed to update status. Check console.");
      // Remove from clickedButtons on failure
      setClickedButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(idx);
        return newSet;
      });
    }
  };
  const [dashboardData, setDashboardData] = useState({});
  const [operations, setOperations] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingOperations, setLoadingOperations] = useState(true);
  const fetchDashboardMetrics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard`);
      const data = await res.json();
      if (data.status === 'success') {
        setDashboardData(data.data || {});
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };
  const fetchNurseNotifications = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_nurse_notifications`);
      const data = await response.json();

      console.log('Count of reviewed notifications:', count);
      if (data.notifications) {

        console.log('is_finalized values:', nurseNotifications.map(n => n.is_finalized));
        const pendingNotifications = data.notifications.filter(
          notification => notification.status === 'pending'
        );

        setNurseNotifications(pendingNotifications);
      } else {
        console.error('Nurse notifications fetch failed:', data.message);

      }
    } catch (err) {
      console.error('Error fetching nurse notifications:', err);
    }
  };

  const fetchOtStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ot_status`);
      const result = await response.json();
      if (result.status === 'success') {
        setOtStatusData(result.data);
      } else {
        // Fallback data if API doesn't exist or returns error
        console.warn('OT status API not available, using fallback data');
        setOtStatusData([
          { ot: 1, status: 'Available', patient: null, end_time: null },
          { ot: 2, status: 'Occupied', patient: 'John Doe', end_time: '14:30' },
          { ot: 3, status: 'Available', patient: null, end_time: null },
          { ot: 4, status: 'Maintenance', patient: null, end_time: null }
        ]);
      }
    } catch (error) {
      console.error('Error fetching OT status:', error);
      // Fallback data on error
      setOtStatusData([
        { ot: 1, status: 'Available', patient: null, end_time: null },
        { ot: 2, status: 'Occupied', patient: 'John Doe', end_time: '14:30' },
        { ot: 3, status: 'Available', patient: null, end_time: null },
        { ot: 4, status: 'Maintenance', patient: null, end_time: null }
      ]);
    }
  };

  const fetchOtUtilization = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ot_utilization`);
      const result = await response.json();
      if (result.status === 'success') {
        setOtUtilizationData(result.data);

      }
    } catch (error) {
      console.error('Error fetching OT utilization:', error);
    }
  };

  const fetchTodaysOperations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/today_operations`);
      const result = await response.json();
      if (result.status === 'success') {
        setTodaysOperations(result.data);
      }
    } catch (error) {
      console.error('Error fetching today\'s operations:', error);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDashboardMetrics(),
          fetchNurseNotifications(),
          fetchTodaysOperations(),
          fetchOtUtilization(),
          fetchOtStatus(),
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchAllData();
    const interval = setInterval(fetchAllData, 30000); // Auto-refresh all data every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleAddTodo = () => {
    if (newTodo.trim() === '') return;

    setNurseTodos(prev => [
      ...prev,
      {
        id: prev.length + 1,
        task: newTodo,
        completed: false,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
    setNewTodo('');
  };

  const handleTodoToggle = (id) => {
    setNurseTodos(prev =>
      prev.map(todo =>
        todo.id === id ? { ...todo, completed: !todo.completed } : todo
      )
    );
  };

  return (
    <div className="space-y-6 p-4">
      <style>{buttonStyles}</style>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <MdAssignment className="w-8 h-8" />
              <div>
                <p className="text-purple-100">Pending Tasks</p>
                <h3 className="text-2xl font-bold">
                  {nurseNotifications.length}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <MdSchedule className="w-8 h-8" />
              <div>
                <p className="text-blue-100">Today's Surgeries</p>
                <h3 className="text-2xl font-bold">{todaysOperations.length}</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <MdCheckCircle className="w-8 h-8" />
              <div>

                <p className="text-green-100">Completed Tasks</p>
                <h3 className="text-2xl font-bold">
                  {dashboardData.task_completed || 0}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pre-operative Orders */}
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Urgent Pre-operative Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {nurseNotifications.length > 0 ? (
              nurseNotifications.map((notification, idx) => (
                <div
                  key={idx}
                  className={`border-l-4 p-3 mb-2 rounded text-sm ${notification.priority === 'Urgent'
                    ? 'bg-red-50 border-red-500'
                    : notification.priority === 'Past Due'
                      ? 'bg-orange-50 border-orange-500'
                      : 'bg-blue-50 border-blue-500'
                    }`}
                >
                  <div className={`font-semibold ${notification.priority === 'Urgent'
                    ? 'text-red-800'
                    : notification.priority === 'Past Due'
                      ? 'text-orange-800'
                      : 'text-blue-800'
                    }`}>
                    {notification.patient_name} (UHID: {notification.uhid})
                  </div>
                  <div className={`${notification.priority === 'Urgent'
                    ? 'text-red-600'
                    : notification.priority === 'Past Due'
                      ? 'text-orange-600'
                      : 'text-blue-600'
                    }`}>
                    <strong>Surgery:</strong> {notification.proposed_surgery}
                  </div>
                  <div className={`${notification.priority === 'Urgent'
                    ? 'text-red-600'
                    : notification.priority === 'Past Due'
                      ? 'text-orange-600'
                      : 'text-blue-600'
                    }`}>
                    <strong>Scheduled:</strong> {notification.scheduled_datetime}
                  </div>
                  <div className={`${notification.priority === 'Urgent'
                    ? 'text-red-600'
                    : notification.priority === 'Past Due'
                      ? 'text-orange-600'
                      : 'text-blue-600'
                    }`}>
                    <strong>Room:</strong> {notification.room} | <strong>Surgeon:</strong> {notification.surgeon_name}
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${notification.priority === 'Urgent'
                      ? 'bg-red-100 text-red-800'
                      : notification.priority === 'Past Due'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-blue-100 text-blue-800'
                      }`}>
                      {notification.priority}
                    </span>

                    {notification.status !== 'reviewed' && (
                      <button
                        onClick={() => updateFormStatus(notification.uhid, idx)}
                        className={`review-button ml-4 bg-green-600 text-white text-xs px-3 py-1 rounded hover:bg-green-700 transition ${clickedButtons.has(idx) ? 'review-button--clicked' : ''
                          }`}
                        disabled={clickedButtons.has(idx)}
                      >
                        ✅ Mark as Reviewed
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No urgent pre-operative orders.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-8">
        {/* Today's Schedule */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Today's Surgery Schedule
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Patient</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Surgery</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">OT</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Surgeon</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {todaysOperations.length > 0 ? (
                  todaysOperations.map((surgery, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{surgery.time}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{surgery.patient}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{surgery.surgery}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{surgery.ot}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{surgery.surgeon}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${surgery.status === 'ongoing'
                          ? 'bg-yellow-100 text-yellow-800'
                          : surgery.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}>
                          {surgery.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No operations scheduled for today
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* OT Status */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Operation Theatre Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otStatusData.map((ot) => (
              <div key={ot.ot} className={`p-4 rounded-lg border-2 ${ot.status === 'Occupied' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">OT-{ot.ot}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${ot.status === 'Occupied' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                    {ot.status}
                  </span>
                </div>
                {ot.status === 'Occupied' && ot.patient && (
                  <div className="mt-2 text-sm text-gray-600">
                    <div>Patient: {ot.patient}</div>
                    {ot.end_time && <div>Est. End: {ot.end_time}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NurseDashboard;